import { type Theme, type EventDetails, type UserProfile } from '@parkingbase/ecommerce-api-test';

/** Discount-program roles that grant Parking Validation access (legacy parity). */
const PARKING_VALIDATION_ROLES = ['ADMIN', 'SUPER_ADMIN', 'USER'];

/**
 * Determines whether the Parking Validation nav item (top navigation board and
 * account sidebar) should be visible for the current user.
 *
 * Mirrors the legacy reservation-site `AppHeader` gate
 * (`isSpecialRateProgramOrRolePresent() || isAnyEventPresent()`): the item is
 * shown only when a user is signed in AND any of the following holds for an
 * in-scope company:
 *  - they hold an `ADMIN` / `SUPER_ADMIN` / `USER` role on a discount program,
 *  - they are a member of a special-rate program (`specialRateProgramMap`), or
 *  - at least one parking-validation event is available to them.
 *
 * Returns `false` when signed out so the tab never persists for an
 * unauthenticated session (PB-251, PB-1395).
 *
 * On converge sites, role/membership are evaluated across every company in
 * `companyThemes`; `events` is already flattened across companies by
 * `getEventsForUser()`.
 *
 * ## Invariants:
 * - Role and membership are evaluated independently. The legacy non-converge
 *   branch required `specialRateProgramMap` to exist before it checked roles,
 *   which could hide the tab from a legitimate role-holder; that precondition
 *   is intentionally dropped here.
 *
 * @param user signed-in user profile (null/undefined when signed out)
 * @param theme active company theme
 * @param companyThemes converge company themes keyed by companyId
 * @param events parking-validation events available to the user
 * @returns true when the Parking Validation nav item should be shown
 */
export function isParkingValidationVisible(
  user: UserProfile | null | undefined,
  theme: Theme | null | undefined,
  companyThemes: Record<string, Theme> | null | undefined,
  events: EventDetails[] | null | undefined
): boolean {
  if (!user) return false;

  // events are already flattened across companies by getEventsForUser()
  if ((events?.length ?? 0) > 0) return true;

  const isConverge = !!(theme as (Theme & { isConvergeWebsite?: boolean }) | null)
    ?.isConvergeWebsite;
  const companyId = (theme as (Theme & { companyId?: string }) | null)?.companyId;
  const companyIds = isConverge ? Object.keys(companyThemes ?? {}) : companyId ? [companyId] : [];
  if (companyIds.length === 0) return false;

  const roles = user.discountProgramRoles ?? {};
  const memberships = user.specialRateProgramMap ?? {};

  return companyIds.some((cId) => {
    const programRoleMap = roles[cId];
    const hasRole =
      !!programRoleMap &&
      Object.values(programRoleMap).some((roleList) =>
        roleList?.some((role) => PARKING_VALIDATION_ROLES.includes(role))
      );
    const hasMembership = (memberships[cId]?.length ?? 0) > 0;
    return hasRole || hasMembership;
  });
}
